export type DrinkMeasurement = {
  volumeMl: number;
  abv: number;
};

export type DrinkCategory = 'Bier' | 'Wein' | 'Cocktail' | 'Sonstiges';

/** New events use one app-wide value; legacy group defaults remain untouched for rollback. */
export const GLOBAL_DEFAULT_BEER_ABV = 5;

interface DrinkDefinition {
  id: string;
  category: Exclude<DrinkCategory, 'Sonstiges'>;
  label: string;
  shortLabel: string;
  volumeLabel: string;
  volumeMl: number;
  /** Beer definitions resolve their ABV from the active group setting. */
  abv: number | null;
}

/** Canonical drink definitions shared by counter UI, history labels and Wrapped categories. */
export const DRINK_DEFINITIONS = {
  beer: {
    330: {
      id: 'beer-330',
      category: 'Bier',
      label: '0,33 Liter Bier',
      shortLabel: 'Bier',
      volumeLabel: '0,33 l',
      volumeMl: 330,
      abv: null,
      description: '0,33 l Bier',
    },
    500: {
      id: 'beer-500',
      category: 'Bier',
      label: '0,5 Liter Bier',
      shortLabel: 'Bier',
      volumeLabel: '0,5 l',
      volumeMl: 500,
      abv: null,
      description: '0,5 l Bier',
    },
    660: {
      id: 'beer-660',
      category: 'Bier',
      label: '0,66 Liter Bier',
      shortLabel: 'Bier',
      volumeLabel: '0,66 l',
      volumeMl: 660,
      abv: null,
      description: '0,66 l Bier',
    },
    1000: {
      id: 'beer-1000',
      category: 'Bier',
      label: '1,0 Liter Bier',
      shortLabel: 'Bier',
      volumeLabel: '1,0 l',
      volumeMl: 1_000,
      abv: null,
      description: '1,0 l Bier',
    },
  },
  cocktail: {
    id: 'cocktail',
    category: 'Cocktail',
    label: 'Cocktail mit 6 cl Spirituose',
    shortLabel: 'Cocktail',
    volumeLabel: '6 cl',
    volumeMl: 60,
    abv: 40,
    description: 'Cocktail',
  },
  wine: {
    id: 'wine-100',
    category: 'Wein',
    label: '0,1 Liter Wein',
    shortLabel: 'Wein',
    volumeLabel: '0,1 l',
    volumeMl: 100,
    abv: 12,
    description: '0,1 l Wein',
  },
} as const satisfies {
  beer: Readonly<Record<330 | 500 | 660 | 1000, DrinkDefinition & { description: string }>>;
  cocktail: DrinkDefinition & { description: string };
  wine: DrinkDefinition & { description: string };
};

const BEER_DEFINITIONS = Object.values(DRINK_DEFINITIONS.beer);
const FIXED_DRINK_DEFINITIONS = [DRINK_DEFINITIONS.cocktail, DRINK_DEFINITIONS.wine];

export function classifyDrink(
  event: DrinkMeasurement,
  beerAbv = GLOBAL_DEFAULT_BEER_ABV,
): DrinkCategory {
  if (isBeerMeasurement(event, beerAbv)) return 'Bier';
  return (
    FIXED_DRINK_DEFINITIONS.find(
      (definition) => definition.volumeMl === event.volumeMl && definition.abv === event.abv,
    )?.category ?? 'Sonstiges'
  );
}

export function describeDrink(event: DrinkMeasurement, beerAbv: number): string {
  const category = classifyDrink(event, beerAbv);
  if (category === 'Bier') {
    const beerDefinition = BEER_DEFINITIONS.find(
      (definition) => definition.volumeMl === event.volumeMl,
    );
    if (beerDefinition) return beerDefinition.description;
  }
  const fixedDefinition = FIXED_DRINK_DEFINITIONS.find(
    (definition) => definition.volumeMl === event.volumeMl && definition.abv === event.abv,
  );
  if (fixedDefinition) return fixedDefinition.description;
  return `${event.volumeMl} ml · ${event.abv.toLocaleString('de-DE')} % vol`;
}

export function isCocktailDrink(event: DrinkMeasurement): boolean {
  return classifyDrink(event) === 'Cocktail';
}

export function isBeerVolume(volumeMl: number): boolean {
  return BEER_DEFINITIONS.some((definition) => definition.volumeMl === volumeMl);
}

function isBeerMeasurement(event: DrinkMeasurement, beerAbv: number): boolean {
  return event.abv === beerAbv && isBeerVolume(event.volumeMl);
}
